import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  ChevronDown,
  ChevronRight,
  Equal,
  ExternalLink,
  Loader2,
  PieChart,
  RotateCcw,
} from "lucide-react";

import Button from "../../../Components/Button";
import TabsComponent from "../../../Components/Tab";
import DataCoverageAuditPanel from "../../../intelligence-briefing-library/components/hawkeye/DataCoverageAuditPanel";
import { getCategoryLabel } from "../../../intelligence-briefing-library/constants/kpiCategories";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import {
  fetchMetricsOrgConfiguration,
  updateMetricsOrgConfiguration,
} from "../../../Store/Reducers/MetricsSlice";
import IngestionCadenceSection, {
  normalizeCadenceOption,
} from "../IngestionCadenceSection";

interface MetricsConfiguration {
  includedCategories: string[];
  categoryWeights: Record<string, number>;
  metricWeights: Record<string, Record<string, number>>;
  includedMetrics: Record<string, string[]>;
  categoryOrder: string[];
}

interface CategoryMetricsSnapshot {
  includedMetrics: string[];
  metricWeights: Record<string, number>;
}

const EMPTY_METRICS_CONFIGURATION: MetricsConfiguration = {
  includedCategories: [],
  categoryWeights: {},
  metricWeights: {},
  includedMetrics: {},
  categoryOrder: [],
};

interface NormalizedMetricItem {
  metricId: string;
  metricName: string;
  description: string | null;
  cadence: string;
  coverageLabel: string;
  isCustom: boolean;
}

interface OrgConfigCategoryMetric {
  name: string;
  weight: number;
  enabled: boolean;
  heading?: string;
  description?: string;
  cadence?: string;
  source?: string;
  coverage?: number | string;
  metric_status?: string;
  last_ingested?: string;
  coverage_detail?: {
    last_ingested?: string;
    coverage_pct_total_accounts?: number;
    coverage_pct_timeseries_accounts?: number;
  };
}

interface OrgConfigCategory {
  name: string;
  weight: number;
  enabled: boolean;
  metrics: OrgConfigCategoryMetric[];
}

const toReadableLabel = (key: string) =>
  key
    .split("_")
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const getMetricHeading = (metric: OrgConfigCategoryMetric) => {
  const metricId = String(metric.name || "");
  if (typeof metric.heading === "string" && metric.heading.trim()) {
    return metric.heading.trim();
  }
  return toReadableLabel(metricId);
};

const getCoverageValue = (
  metric?: Pick<OrgConfigCategoryMetric, "coverage">
): string => {
  if (typeof metric?.coverage === "number") return String(metric.coverage);
  if (typeof metric?.coverage === "string") return metric.coverage.trim();
  return "";
};

const normalizeOrgConfigMetric = (
  metric: OrgConfigCategoryMetric
): NormalizedMetricItem => {
  const metricId = String(metric.name || "");
  const description =
    typeof metric.description === "string" && metric.description.trim()
      ? metric.description.trim()
      : null;
  return {
    metricId,
    metricName: getMetricHeading(metric),
    description,
    cadence:
      typeof metric.cadence === "string" && metric.cadence.trim()
        ? metric.cadence.trim()
        : "n/a",
    coverageLabel: getCoverageValue(metric) || "n/a",
    isCustom: String(metric.source || "").toLowerCase() === "custom",
  };
};

const parseCoveragePercent = (coverage?: number | string): number | null => {
  if (coverage === null || coverage === undefined) return null;
  if (typeof coverage === "number") {
    return Number.isFinite(coverage) ? coverage : null;
  }
  const trimmed = String(coverage).trim().toLowerCase();
  if (!trimmed || trimmed === "n/a" || trimmed === "na") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const COVERAGE_STATUS = {
  active: { label: "Active", pill: "bg-emerald-100 text-emerald-700" },
  partial: { label: "Partial", pill: "bg-amber-100 text-amber-900" },
  no_data: { label: "No data", pill: "bg-slate-100 text-slate-600" },
} as const;

type CoverageStatus = keyof typeof COVERAGE_STATUS;

const COVERAGE_STATUS_ORDER: CoverageStatus[] = [
  "active",
  "partial",
  "no_data",
];

const deriveCoverageStatus = (coverage?: number | string): CoverageStatus => {
  const pct = parseCoveragePercent(coverage);
  if (pct === null || pct === 0) return "no_data";
  if (pct === 100) return "active";
  return "partial";
};

const getMetricCoverageDisplay = (metric: NormalizedMetricItem) => {
  const coverage =
    metric.coverageLabel === "n/a" ? undefined : metric.coverageLabel;
  return COVERAGE_STATUS[deriveCoverageStatus(coverage)];
};

const buildCategorySummary = (metrics: NormalizedMetricItem[]) => {
  const counts: Record<CoverageStatus, number> = {
    active: 0,
    partial: 0,
    no_data: 0,
  };
  metrics.forEach(m => {
    const coverage = m.coverageLabel === "n/a" ? undefined : m.coverageLabel;
    counts[deriveCoverageStatus(coverage)] += 1;
  });
  const total = Math.max(1, metrics.length);
  const summaryText = COVERAGE_STATUS_ORDER.filter(key => counts[key] > 0)
    .map(key => `${counts[key]} ${COVERAGE_STATUS[key].label.toLowerCase()}`)
    .join(" · ");
  return {
    summaryText,
    activePct: (counts.active / total) * 100,
    partialPct: (counts.partial / total) * 100,
    noDataPct: (counts.no_data / total) * 100,
  };
};

const buildMetricsByCategory = (
  categories: OrgConfigCategory[]
): Record<string, NormalizedMetricItem[]> => {
  const grouped: Record<string, NormalizedMetricItem[]> = {};
  categories.forEach(category => {
    const categoryId = String(category?.name || "");
    if (!categoryId) return;
    grouped[categoryId] = (
      Array.isArray(category.metrics) ? category.metrics : []
    )
      .filter(metric => Boolean(metric?.name))
      .map(normalizeOrgConfigMetric);
  });
  return grouped;
};

const sanitizeWeightInput = (value: string): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.min(100, Math.round(parsed)));
};

const roundWeight = (n: number) => Math.round(n);

/** Distribute `total` across `items` by current ratios (or equally if all zero). */
const distributeProportionally = (
  items: string[],
  currentWeights: Record<string, number>,
  total: number
): Record<string, number> => {
  if (items.length === 0) return {};

  const totalCurrent = items.reduce(
    (sum, id) => sum + (currentWeights[id] || 0),
    0
  );
  const result: Record<string, number> = {};

  if (totalCurrent > 0 && total > 0) {
    let distributed = 0;
    items.forEach((id, index) => {
      if (index === items.length - 1) {
        result[id] = roundWeight(Math.max(0, total - distributed));
      } else {
        const proportion = (currentWeights[id] || 0) / totalCurrent;
        const weight = roundWeight(Math.max(0, total * proportion));
        result[id] = weight;
        distributed += weight;
      }
    });
  } else if (total <= 0) {
    items.forEach(id => {
      result[id] = 0;
    });
  } else {
    let distributed = 0;
    items.forEach((id, index) => {
      if (index === items.length - 1) {
        result[id] = roundWeight(total - distributed);
      } else {
        const weight = roundWeight(total / items.length);
        result[id] = weight;
        distributed += weight;
      }
    });
  }

  return result;
};

const equalDistributeWeights = (items: string[]): Record<string, number> => {
  if (items.length === 0) return {};
  let distributed = 0;
  const result: Record<string, number> = {};
  items.forEach((id, index) => {
    if (index === items.length - 1) {
      result[id] = roundWeight(100 - distributed);
    } else {
      const weight = roundWeight(100 / items.length);
      result[id] = weight;
      distributed += weight;
    }
  });
  return result;
};

const buildConfigurationFromOverride = (
  overrideCategories: OrgConfigCategory[]
): MetricsConfiguration => {
  const categoryWeights: Record<string, number> = {};
  const includedCategories: string[] = [];
  const includedMetrics: Record<string, string[]> = {};
  const metricWeights: Record<string, Record<string, number>> = {};
  const categoryOrder: string[] = [];

  overrideCategories.forEach(category => {
    const categoryName = String(category?.name || "");
    if (!categoryName) return;
    categoryOrder.push(categoryName);
    categoryWeights[categoryName] = Number(category?.weight) || 0;
    if (category?.enabled !== false) includedCategories.push(categoryName);

    const metrics = Array.isArray(category?.metrics) ? category.metrics : [];
    metricWeights[categoryName] = {};
    includedMetrics[categoryName] = [];

    metrics.forEach(metric => {
      const metricName = String(metric?.name || "");
      if (!metricName) return;
      metricWeights[categoryName][metricName] = Number(metric?.weight) || 0;
      if (metric?.enabled !== false)
        includedMetrics[categoryName].push(metricName);
    });
  });

  return {
    includedCategories,
    categoryWeights,
    metricWeights,
    includedMetrics,
    categoryOrder,
  };
};

const buildConfigurationFromEffective = (
  effective: Record<string, unknown> | null | undefined
): MetricsConfiguration | null => {
  if (!effective || typeof effective !== "object") return null;

  const nestedCategories = effective.categories;
  if (Array.isArray(nestedCategories) && nestedCategories.length > 0) {
    return buildConfigurationFromOverride(
      nestedCategories as OrgConfigCategory[]
    );
  }

  const categoryWeights = (effective.category_weights ||
    effective.categoryWeights ||
    {}) as Record<string, number>;
  if (!Object.keys(categoryWeights).length) return null;

  return {
    includedCategories: (effective.included_categories ||
      effective.includedCategories ||
      Object.keys(categoryWeights).filter(
        key => key !== "overall"
      )) as string[],
    categoryWeights,
    metricWeights: (effective.metric_weights ||
      effective.metricWeights ||
      {}) as Record<string, Record<string, number>>,
    includedMetrics: (effective.included_metrics ||
      effective.includedMetrics ||
      {}) as Record<string, string[]>,
    categoryOrder: (effective.category_order ||
      effective.categoryOrder ||
      Object.keys(categoryWeights)) as string[],
  };
};

const resolveOrgConfigCategories = (
  orgData: Record<string, unknown> | null | undefined
): OrgConfigCategory[] => {
  if (!orgData || typeof orgData !== "object") return [];
  const config = orgData.config as
    | {
        categories?: OrgConfigCategory[];
        config_override?: { categories?: OrgConfigCategory[] };
      }
    | undefined;
  const legacy = orgData.config_override as
    | { categories?: OrgConfigCategory[] }
    | undefined;
  const effective = orgData.effective_config as
    | { categories?: OrgConfigCategory[] }
    | undefined;
  const candidates = [
    config?.config_override?.categories,
    legacy?.categories,
    config?.categories,
    effective?.categories,
  ];
  for (const categories of candidates) {
    if (Array.isArray(categories) && categories.length > 0) {
      return categories;
    }
  }
  return [];
};

const resolveUseSyntheticFromOrgConfig = (
  orgData: Record<string, unknown> | null | undefined
): boolean => {
  if (!orgData || typeof orgData !== "object") return false;
  const config = (orgData.config as Record<string, unknown> | undefined) || {};
  if (typeof config.use_synthetic_data === "boolean") {
    return config.use_synthetic_data;
  }
  const nested = config.config_override as Record<string, unknown> | undefined;
  if (typeof nested?.use_synthetic_data === "boolean") {
    return nested.use_synthetic_data;
  }
  const legacy = orgData.config_override as Record<string, unknown> | undefined;
  return Boolean(legacy?.use_synthetic_data);
};

const buildConfigurationFromLegacyOrg = (
  orgData: Record<string, unknown>
): MetricsConfiguration => {
  const fromEffective = buildConfigurationFromEffective(
    (orgData.effective_config || orgData.effectiveConfig) as
      | Record<string, unknown>
      | undefined
  );
  if (fromEffective) return fromEffective;

  const weights = (orgData.category_weights ||
    orgData.categoryWeights ||
    {}) as Record<string, number>;
  return {
    includedCategories: (orgData.included_categories ||
      orgData.includedCategories ||
      []) as string[],
    categoryWeights: weights,
    metricWeights: (orgData.metric_weights ||
      orgData.metricWeights ||
      {}) as Record<string, Record<string, number>>,
    includedMetrics: (orgData.included_metrics ||
      orgData.includedMetrics ||
      {}) as Record<string, string[]>,
    categoryOrder: (orgData.category_order ||
      orgData.categoryOrder ||
      Object.keys(weights)) as string[],
  };
};

const buildCadenceByMetricKey = (
  categories: OrgConfigCategory[],
  normalizeCadence: (value?: string) => string
): Record<string, string> => {
  const next: Record<string, string> = {};
  categories.forEach(category => {
    const categoryName = String(category?.name || "");
    if (!categoryName) return;
    (Array.isArray(category.metrics) ? category.metrics : []).forEach(
      metric => {
        const metricName = String(metric?.name || "");
        if (!metricName) return;
        next[`${categoryName}::${metricName}`] = normalizeCadence(
          metric?.cadence
        );
      }
    );
  });
  return next;
};

interface MetricsWeightsSectionProps {
  onViewEnterpriseKpis?: () => void;
}

const WEIGHT_ACTION_BTN_CLASS =
  "!min-h-0 gap-1.5 px-3 py-1.5 !text-xs [&>div]:!mr-0";

const WeightRedistributionActions: React.FC<{
  showRedistribute: boolean;
  disabled?: boolean;
  onProRata: () => void;
  onEqual: () => void;
  onDiscard: () => void;
}> = ({ showRedistribute, disabled, onProRata, onEqual, onDiscard }) => (
  <div className="flex flex-wrap items-center gap-2">
    {showRedistribute && (
      <>
        <Button
          type="button"
          variant="outline-primary"
          className={WEIGHT_ACTION_BTN_CLASS}
          disabled={disabled}
          icon={<PieChart className="h-3.5 w-3.5" aria-hidden />}
          onClick={onProRata}
          title="Redistribute weights proportionally by current ratios"
        >
          Pro Rata
        </Button>
        <Button
          type="button"
          variant="outline-primary"
          className={WEIGHT_ACTION_BTN_CLASS}
          disabled={disabled}
          icon={<Equal className="h-3.5 w-3.5" aria-hidden />}
          onClick={onEqual}
          title="Split weights equally across included items"
        >
          Equal
        </Button>
      </>
    )}
    <Button
      type="button"
      variant="outline-primary"
      className={WEIGHT_ACTION_BTN_CLASS}
      disabled={disabled}
      icon={<RotateCcw className="h-3.5 w-3.5" aria-hidden />}
      onClick={onDiscard}
      title="Revert to last saved values"
    >
      Discard
    </Button>
  </div>
);

const MetricsWeightsSection: React.FC<MetricsWeightsSectionProps> = ({
  onViewEnterpriseKpis,
}) => {
  type ConfigSubTab = "kpiConfiguration" | "dataAudit" | "ingestionCadence";

  const dispatch = useAppDispatch();
  const {
    loading: isConfigLoading,
    error: configError,
    data: orgConfigurationData,
  } = useAppSelector(state => state.metrics.orgConfiguration);
  const { loading: isSaving, error: saveError } = useAppSelector(
    state => state.metrics.orgConfigurationUpdate
  );
  const [configuration, setConfiguration] = useState<MetricsConfiguration>(
    EMPTY_METRICS_CONFIGURATION
  );
  const [draftConfiguration, setDraftConfiguration] =
    useState<MetricsConfiguration>(EMPTY_METRICS_CONFIGURATION);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );
  const [activeSubTab, setActiveSubTab] =
    useState<ConfigSubTab>("kpiConfiguration");
  const [cadenceByMetricKey, setCadenceByMetricKey] = useState<
    Record<string, string>
  >({});
  const [initialCadenceByMetricKey, setInitialCadenceByMetricKey] = useState<
    Record<string, string>
  >({});
  const [categoryWeightsTouched, setCategoryWeightsTouched] = useState(false);
  const [metricWeightsTouchedByCategory, setMetricWeightsTouchedByCategory] =
    useState<Record<string, boolean>>({});
  const categoryMetricsSnapshotRef = useRef<
    Record<string, CategoryMetricsSnapshot>
  >({});

  useEffect(() => {
    void dispatch(fetchMetricsOrgConfiguration());
  }, [dispatch]);

  useEffect(() => {
    if (!orgConfigurationData) return;

    const overrideCategories = resolveOrgConfigCategories(
      orgConfigurationData as Record<string, unknown>
    );

    const nextConfiguration = overrideCategories.length
      ? buildConfigurationFromOverride(overrideCategories)
      : buildConfigurationFromLegacyOrg(
          orgConfigurationData as Record<string, unknown>
        );

    setConfiguration(nextConfiguration);
    setDraftConfiguration(nextConfiguration);
    setCategoryWeightsTouched(false);
    setMetricWeightsTouchedByCategory({});
    categoryMetricsSnapshotRef.current = {};

    const cadenceMap = buildCadenceByMetricKey(
      overrideCategories,
      normalizeCadenceOption
    );
    setCadenceByMetricKey(cadenceMap);
    setInitialCadenceByMetricKey(cadenceMap);
  }, [orgConfigurationData]);

  const totalWeight = useMemo(
    () =>
      draftConfiguration.includedCategories.reduce<number>(
        (sum, categoryId) =>
          sum + (Number(draftConfiguration.categoryWeights[categoryId]) || 0),
        0
      ),
    [draftConfiguration]
  );

  const metricWeightTotalByCategory = useMemo(() => {
    const totals: Record<string, number> = {};
    const categoryIds = new Set<string>([
      ...Object.keys(draftConfiguration.metricWeights || {}),
      ...Object.keys(draftConfiguration.includedMetrics || {}),
    ]);
    categoryIds.forEach(categoryId => {
      const enabled = draftConfiguration.includedMetrics[categoryId] || [];
      totals[categoryId] = enabled.reduce(
        (sum, metricId) =>
          sum +
          (Number(draftConfiguration.metricWeights[categoryId]?.[metricId]) ||
            0),
        0
      );
    });
    return totals;
  }, [draftConfiguration]);

  const orderedCategories = useMemo(() => {
    const categoryWeights = draftConfiguration.categoryWeights || {};
    const ordered = (draftConfiguration.categoryOrder || []).filter(
      category => category !== "overall" && category in categoryWeights
    );
    const remaining = Object.keys(categoryWeights).filter(
      category => category !== "overall" && !ordered.includes(category)
    );
    return [...ordered, ...remaining];
  }, [draftConfiguration]);

  const metricsByCategory = useMemo(
    () =>
      buildMetricsByCategory(
        resolveOrgConfigCategories(
          orgConfigurationData as Record<string, unknown> | null | undefined
        )
      ),
    [orgConfigurationData]
  );

  const hasChanges = useMemo(
    () =>
      JSON.stringify({
        categoryWeights: draftConfiguration.categoryWeights,
        includedCategories: draftConfiguration.includedCategories,
        includedMetrics: draftConfiguration.includedMetrics,
        categoryOrder: draftConfiguration.categoryOrder,
        metricWeights: draftConfiguration.metricWeights,
      }) !==
      JSON.stringify({
        categoryWeights: configuration.categoryWeights,
        includedCategories: configuration.includedCategories,
        includedMetrics: configuration.includedMetrics,
        categoryOrder: configuration.categoryOrder,
        metricWeights: configuration.metricWeights,
      }),
    [configuration, draftConfiguration]
  );
  const hasCadenceChanges = useMemo(
    () =>
      JSON.stringify(cadenceByMetricKey) !==
      JSON.stringify(initialCadenceByMetricKey),
    [cadenceByMetricKey, initialCadenceByMetricKey]
  );
  const hasAnyChanges = hasChanges || hasCadenceChanges;

  const categoryWeightsValid = totalWeight === 100;

  const metricWeightsValid = useMemo(() => {
    return draftConfiguration.includedCategories.every(categoryId => {
      const enabled = draftConfiguration.includedMetrics[categoryId] || [];
      if (enabled.length === 0) return true;
      return (metricWeightTotalByCategory[categoryId] ?? 0) === 100;
    });
  }, [draftConfiguration, metricWeightTotalByCategory]);

  const weightsValid = categoryWeightsValid && metricWeightsValid;

  const hasCategoryWeightsChanges = useMemo(
    () =>
      JSON.stringify({
        includedCategories: [...draftConfiguration.includedCategories].sort(),
        categoryWeights: draftConfiguration.categoryWeights,
      }) !==
      JSON.stringify({
        includedCategories: [...configuration.includedCategories].sort(),
        categoryWeights: configuration.categoryWeights,
      }),
    [configuration, draftConfiguration]
  );

  const metricConfigChangesByCategory = useMemo(() => {
    const result: Record<string, boolean> = {};
    const categoryIds = new Set<string>([
      ...Object.keys(configuration.metricWeights || {}),
      ...Object.keys(draftConfiguration.metricWeights || {}),
      ...Object.keys(configuration.includedMetrics || {}),
      ...Object.keys(draftConfiguration.includedMetrics || {}),
    ]);
    categoryIds.forEach(categoryId => {
      result[categoryId] =
        JSON.stringify({
          included: [
            ...(draftConfiguration.includedMetrics[categoryId] || []),
          ].sort(),
          weights: draftConfiguration.metricWeights[categoryId] || {},
        }) !==
        JSON.stringify({
          included: [
            ...(configuration.includedMetrics[categoryId] || []),
          ].sort(),
          weights: configuration.metricWeights[categoryId] || {},
        });
    });
    return result;
  }, [configuration, draftConfiguration]);

  const persistConfiguration = async (
    nextConfiguration: MetricsConfiguration
  ) => {
    const configurationId =
      typeof orgConfigurationData?.id === "string"
        ? orgConfigurationData.id
        : "";
    if (!configurationId) return;

    const orderedCategoryNames = [
      ...(nextConfiguration.categoryOrder || []),
      ...Object.keys(nextConfiguration.categoryWeights || {}).filter(
        categoryName =>
          !(nextConfiguration.categoryOrder || []).includes(categoryName)
      ),
    ];

    const overrideCategories = resolveOrgConfigCategories(
      orgConfigurationData as Record<string, unknown> | null | undefined
    );
    const overrideMetricByCategory: Record<
      string,
      Record<string, OrgConfigCategoryMetric>
    > = {};
    overrideCategories.forEach(category => {
      const categoryName = String(category?.name || "");
      if (!categoryName) return;
      const byMetric: Record<string, OrgConfigCategoryMetric> = {};
      (Array.isArray(category?.metrics) ? category.metrics : []).forEach(
        metric => {
          const metricName = String(metric?.name || "");
          if (!metricName) return;
          byMetric[metricName] = metric;
        }
      );
      overrideMetricByCategory[categoryName] = byMetric;
    });

    const categories: OrgConfigCategory[] = orderedCategoryNames
      .filter(Boolean)
      .map(categoryName => {
        const metricNameSet = new Set<string>([
          ...Object.keys(nextConfiguration.metricWeights?.[categoryName] || {}),
          ...(nextConfiguration.includedMetrics?.[categoryName] || []),
        ]);

        const metrics: OrgConfigCategoryMetric[] = Array.from(
          metricNameSet
        ).map(metricName => {
          const existingOverrideMetric =
            overrideMetricByCategory?.[categoryName]?.[metricName];
          return {
            name: metricName,
            weight:
              Number(
                nextConfiguration.metricWeights?.[categoryName]?.[metricName]
              ) || 0,
            enabled: (
              nextConfiguration.includedMetrics?.[categoryName] || []
            ).includes(metricName),
            cadence:
              cadenceByMetricKey[`${categoryName}::${metricName}`] ||
              normalizeCadenceOption(existingOverrideMetric?.cadence) ||
              "manual",
            coverage: existingOverrideMetric?.coverage,
            heading: existingOverrideMetric?.heading,
            description: existingOverrideMetric?.description,
            source: existingOverrideMetric?.source,
            metric_status: existingOverrideMetric?.metric_status,
            last_ingested: existingOverrideMetric?.last_ingested,
            coverage_detail: existingOverrideMetric?.coverage_detail,
          };
        });

        return {
          name: categoryName,
          weight:
            Number(nextConfiguration.categoryWeights?.[categoryName]) || 0,
          enabled: nextConfiguration.includedCategories.includes(categoryName),
          metrics,
        };
      });

    const existingConfig =
      (orgConfigurationData?.config as Record<string, unknown>) || {};
    const existingOverride =
      (existingConfig.config_override as Record<string, unknown>) ||
      (orgConfigurationData?.config_override as Record<string, unknown>) ||
      {};
    const useSyntheticData = resolveUseSyntheticFromOrgConfig(
      orgConfigurationData as Record<string, unknown>
    );
    const categoryEnabled: Record<string, boolean> = {};
    const categoryWeightsFlat: Record<string, number> = {};
    const metricEnabled: Record<string, boolean> = {};
    const metricWeightsFlat: Record<string, number> = {};
    categories.forEach(category => {
      categoryEnabled[category.name] = Boolean(category.enabled);
      categoryWeightsFlat[category.name] = Number(category.weight) || 0;
      (category.metrics || []).forEach(metric => {
        metricEnabled[metric.name] = Boolean(metric.enabled);
        metricWeightsFlat[metric.name] = Number(metric.weight) || 0;
      });
    });
    const result = await dispatch(
      updateMetricsOrgConfiguration({
        configuration_id: configurationId,
        template_name: "default",
        config_version: Number(orgConfigurationData?.config_version) || 1,
        config: {
          ...existingConfig,
          is_master: Boolean(
            existingConfig.is_master ?? orgConfigurationData?.is_master
          ),
          // Keep top-level categories in sync with the draft. Nested
          // config_override alone left effective_config / Always On Channel
          // reading stale weights after save.
          categories,
          categoryEnabled,
          categoryWeights: categoryWeightsFlat,
          metricEnabled,
          metricWeights: metricWeightsFlat,
          use_synthetic_data: useSyntheticData,
          config_override: {
            ...existingOverride,
            use_synthetic_data: useSyntheticData,
            categories,
          },
        },
      })
    );
    if (updateMetricsOrgConfiguration.fulfilled.match(result)) {
      setConfiguration(nextConfiguration);
      setDraftConfiguration(nextConfiguration);
    }
  };

  const redistributeIncludedCategoryWeights = (mode: "proRata" | "equal") => {
    const included = draftConfiguration.includedCategories;
    const allCategoryIds = Object.keys(draftConfiguration.categoryWeights);

    if (included.length === 0) {
      setDraftConfiguration({
        ...draftConfiguration,
        categoryWeights: Object.fromEntries(allCategoryIds.map(id => [id, 0])),
      });
      return;
    }

    const includedWeights =
      mode === "equal"
        ? equalDistributeWeights(included)
        : distributeProportionally(
            included,
            Object.fromEntries(
              included.map(id => [
                id,
                draftConfiguration.categoryWeights[id] || 0,
              ])
            ),
            100
          );

    setDraftConfiguration({
      ...draftConfiguration,
      categoryWeights: Object.fromEntries(
        allCategoryIds.map(id => [
          id,
          included.includes(id) ? (includedWeights[id] ?? 0) : 0,
        ])
      ),
    });
    setCategoryWeightsTouched(true);
  };

  const handleDiscardCategoryWeights = () => {
    categoryMetricsSnapshotRef.current = {};
    setDraftConfiguration({
      ...draftConfiguration,
      includedCategories: [...configuration.includedCategories],
      categoryWeights: { ...configuration.categoryWeights },
      includedMetrics: { ...configuration.includedMetrics },
      metricWeights: { ...configuration.metricWeights },
    });
    setCategoryWeightsTouched(false);
    setMetricWeightsTouchedByCategory({});
  };

  const redistributeIncludedMetricWeights = (
    categoryId: string,
    mode: "proRata" | "equal"
  ) => {
    const included = draftConfiguration.includedMetrics[categoryId] || [];
    const categoryMetricWeights = {
      ...(draftConfiguration.metricWeights[categoryId] || {}),
    };

    if (included.length === 0) {
      Object.keys(categoryMetricWeights).forEach(metricId => {
        categoryMetricWeights[metricId] = 0;
      });
    } else {
      const includedWeights =
        mode === "equal"
          ? equalDistributeWeights(included)
          : distributeProportionally(
              included,
              Object.fromEntries(
                included.map(id => [id, categoryMetricWeights[id] || 0])
              ),
              100
            );

      Object.keys(categoryMetricWeights).forEach(metricId => {
        categoryMetricWeights[metricId] = included.includes(metricId)
          ? (includedWeights[metricId] ?? 0)
          : 0;
      });
      included.forEach(metricId => {
        categoryMetricWeights[metricId] = includedWeights[metricId] ?? 0;
      });
    }

    setDraftConfiguration({
      ...draftConfiguration,
      metricWeights: {
        ...draftConfiguration.metricWeights,
        [categoryId]: categoryMetricWeights,
      },
    });
    setMetricWeightsTouchedByCategory(prev => ({
      ...prev,
      [categoryId]: true,
    }));
  };

  const handleDiscardMetricConfiguration = (categoryId: string) => {
    delete categoryMetricsSnapshotRef.current[categoryId];
    setDraftConfiguration({
      ...draftConfiguration,
      includedMetrics: {
        ...draftConfiguration.includedMetrics,
        [categoryId]: [...(configuration.includedMetrics[categoryId] || [])],
      },
      metricWeights: {
        ...draftConfiguration.metricWeights,
        [categoryId]: {
          ...(configuration.metricWeights[categoryId] || {}),
        },
      },
    });
    setMetricWeightsTouchedByCategory(prev => {
      const next = { ...prev };
      delete next[categoryId];
      return next;
    });
  };

  const handleToggleCategory = (category: string) => {
    const wasIncluded =
      draftConfiguration.includedCategories.includes(category);

    if (wasIncluded) {
      categoryMetricsSnapshotRef.current[category] = {
        includedMetrics: [
          ...(draftConfiguration.includedMetrics[category] || []),
        ],
        metricWeights: {
          ...(draftConfiguration.metricWeights[category] || {}),
        },
      };

      setDraftConfiguration({
        ...draftConfiguration,
        includedCategories: draftConfiguration.includedCategories.filter(
          item => item !== category
        ),
        includedMetrics: {
          ...draftConfiguration.includedMetrics,
          [category]: [],
        },
      });
      return;
    }

    const snapshot = categoryMetricsSnapshotRef.current[category];
    const restoredIncluded = snapshot
      ? [...snapshot.includedMetrics]
      : [...(configuration.includedMetrics[category] || [])];
    let restoredWeights = snapshot
      ? { ...snapshot.metricWeights }
      : { ...(configuration.metricWeights[category] || {}) };

    if (restoredIncluded.length > 0) {
      const seedWeights = Object.fromEntries(
        restoredIncluded.map(id => [id, restoredWeights[id] || 0])
      );
      restoredWeights = {
        ...restoredWeights,
        ...distributeProportionally(restoredIncluded, seedWeights, 100),
      };
    }

    setDraftConfiguration({
      ...draftConfiguration,
      includedCategories: [...draftConfiguration.includedCategories, category],
      includedMetrics: {
        ...draftConfiguration.includedMetrics,
        [category]: restoredIncluded,
      },
      metricWeights: {
        ...draftConfiguration.metricWeights,
        [category]: restoredWeights,
      },
    });
  };

  const handleToggleMetric = (categoryId: string, metricId: string) => {
    const current = draftConfiguration.includedMetrics[categoryId] || [];
    const wasEnabled = current.includes(metricId);
    const nextMetrics = wasEnabled
      ? current.filter(id => id !== metricId)
      : [...current, metricId];

    const categoryMetricWeights = {
      ...(draftConfiguration.metricWeights[categoryId] || {}),
    };

    const nextMetricWeights: Record<string, number> = {
      ...categoryMetricWeights,
    };

    if (nextMetrics.length === 0) {
      Object.keys(nextMetricWeights).forEach(id => {
        nextMetricWeights[id] = 0;
      });
    } else {
      const seedWeights: Record<string, number> = {};
      nextMetrics.forEach(id => {
        seedWeights[id] = wasEnabled
          ? categoryMetricWeights[id] || 0
          : id === metricId
            ? categoryMetricWeights[id] || roundWeight(100 / nextMetrics.length)
            : categoryMetricWeights[id] || 0;
      });

      const redistributed = distributeProportionally(
        nextMetrics,
        seedWeights,
        100
      );

      Object.keys(nextMetricWeights).forEach(id => {
        nextMetricWeights[id] = nextMetrics.includes(id)
          ? (redistributed[id] ?? 0)
          : 0;
      });
      nextMetrics.forEach(id => {
        nextMetricWeights[id] = redistributed[id] ?? 0;
      });
    }

    setDraftConfiguration({
      ...draftConfiguration,
      includedMetrics: {
        ...draftConfiguration.includedMetrics,
        [categoryId]: nextMetrics,
      },
      metricWeights: {
        ...draftConfiguration.metricWeights,
        [categoryId]: nextMetricWeights,
      },
    });
    setMetricWeightsTouchedByCategory(prev => ({
      ...prev,
      [categoryId]: true,
    }));
  };

  const handleMetricWeightChange = (
    categoryId: string,
    metricId: string,
    value: string
  ) => {
    const sanitized = sanitizeWeightInput(value);
    setDraftConfiguration({
      ...draftConfiguration,
      metricWeights: {
        ...draftConfiguration.metricWeights,
        [categoryId]: {
          ...(draftConfiguration.metricWeights[categoryId] || {}),
          [metricId]: sanitized,
        },
      },
    });
  };

  const handleCategoryWeightChange = (categoryId: string, value: string) => {
    const sanitized = sanitizeWeightInput(value);
    setDraftConfiguration({
      ...draftConfiguration,
      categoryWeights: {
        ...draftConfiguration.categoryWeights,
        [categoryId]: sanitized,
      },
    });
  };

  const handleCategoryWeightBlur = () => {
    setCategoryWeightsTouched(true);
  };

  const handleMetricWeightBlur = (categoryId: string) => {
    setMetricWeightsTouchedByCategory(prev => ({
      ...prev,
      [categoryId]: true,
    }));
  };

  const resetWeightValidationState = () => {
    setCategoryWeightsTouched(false);
    setMetricWeightsTouchedByCategory({});
  };

  const handleDiscardChanges = () => {
    setDraftConfiguration(configuration);
    setCadenceByMetricKey(initialCadenceByMetricKey);
    resetWeightValidationState();
  };

  const handleSaveChanges = async () => {
    if (!hasAnyChanges) return;
    if (hasChanges) {
      setCategoryWeightsTouched(true);
      setMetricWeightsTouchedByCategory(prev => {
        const next = { ...prev };
        draftConfiguration.includedCategories.forEach(categoryId => {
          next[categoryId] = true;
        });
        return next;
      });
      if (!weightsValid) return;
    }
    await persistConfiguration(draftConfiguration);
    resetWeightValidationState();
  };

  const handleCadenceChange = (metricKey: string, cadence: string) => {
    setCadenceByMetricKey(prev => ({
      ...prev,
      [metricKey]: normalizeCadenceOption(cadence),
    }));
  };

  const toggleExpandedCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  };

  const getCategorySummary = (categoryId: string) =>
    buildCategorySummary(metricsByCategory[categoryId] || []);

  return (
    <div className="space-y-6">
      {configError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {configError}
        </div>
      )}

      {saveError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {saveError}
        </div>
      )}

      {isConfigLoading ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading configuration...
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-end justify-between gap-3 border-b border-gray-200 bg-white">
            <TabsComponent
              tabs={[
                { id: "kpiConfiguration", label: "KPI Configuration" },
                { id: "dataAudit", label: "Data Audit" },
                { id: "ingestionCadence", label: "Ingestion Cadence" },
              ]}
              activeTab={activeSubTab}
              onTabChange={id => setActiveSubTab(id as ConfigSubTab)}
              variant="tab-underline"
              className="mb-0 min-w-0 flex-1"
            />
            {onViewEnterpriseKpis ? (
              <Button
                type="button"
                variant="outline-primary"
                className="mb-2 inline-flex shrink-0 items-center gap-1.5 text-xs"
                onClick={onViewEnterpriseKpis}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                View KPIs
              </Button>
            ) : null}
          </div>

          {activeSubTab === "dataAudit" && (
            <div>
              <DataCoverageAuditPanel embedded />
            </div>
          )}

          {activeSubTab === "kpiConfiguration" && (
            <>
              <div>
                <h4 className="mb-1 text-lg font-semibold text-gray-900">
                  Category Weights
                </h4>
                <p className="mb-0 text-sm text-gray-600">
                  These control how much each category contributes to the
                  overall KPI score. Custom rubric metrics appear here disabled
                  at weight 0 — enable them and set weights (Equal, pro rata, or
                  manual) when you want them in scoring / Always On.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {orderedCategories.map(category => {
                  const weight =
                    Number(draftConfiguration.categoryWeights[category]) || 0;
                  const clampedWeight = Math.max(0, Math.min(100, weight));
                  const isActive =
                    draftConfiguration.includedCategories.includes(category);
                  const displayWeight = isActive ? clampedWeight : 0;
                  return (
                    <div
                      key={category}
                      className={`rounded-lg border border-gray-200 bg-gray-50 p-3 ${
                        isActive ? "" : "opacity-70"
                      }`}
                    >
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="text-sm font-semibold text-gray-700">
                          {getCategoryLabel(category) ||
                            toReadableLabel(category)}
                        </div>
                        <button
                          type="button"
                          onClick={() => void handleToggleCategory(category)}
                          disabled={isSaving}
                          className={`relative h-6 w-11 rounded-full transition ${
                            isActive ? "bg-violet-500" : "bg-gray-300"
                          } ${isSaving ? "cursor-not-allowed opacity-60" : ""}`}
                          aria-label={`Toggle ${category} category`}
                        >
                          <span
                            className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${
                              isActive ? "left-[22px]" : "left-0.5"
                            }`}
                          />
                        </button>
                      </div>
                      <div className="mb-2 h-1 w-full rounded bg-gray-200">
                        <div
                          className="h-full rounded bg-violet-500"
                          style={{ width: `${displayWeight}%` }}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={1}
                          value={displayWeight}
                          onChange={event =>
                            handleCategoryWeightChange(
                              category,
                              event.target.value
                            )
                          }
                          onBlur={handleCategoryWeightBlur}
                          className="w-14 rounded border border-gray-200 bg-white px-2 py-1 text-sm font-medium text-violet-600 disabled:cursor-not-allowed disabled:bg-gray-100"
                          disabled={!isActive || isSaving}
                        />
                        <span className="text-sm text-gray-500">%</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-1 text-sm text-gray-700">
                  <span className="font-medium">Category weights total:</span>
                  <span>
                    {totalWeight}% {totalWeight === 100 ? "✓" : ""}
                  </span>
                </div>
                {hasCategoryWeightsChanges && (
                  <WeightRedistributionActions
                    showRedistribute={
                      draftConfiguration.includedCategories.length > 0
                    }
                    disabled={isSaving}
                    onProRata={() =>
                      redistributeIncludedCategoryWeights("proRata")
                    }
                    onEqual={() => redistributeIncludedCategoryWeights("equal")}
                    onDiscard={handleDiscardCategoryWeights}
                  />
                )}
              </div>

              {categoryWeightsTouched && !categoryWeightsValid && (
                <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  Category weights must total exactly 100% (currently{" "}
                  {totalWeight}%).
                </div>
              )}

              <div className="overflow-hidden rounded-xl border border-gray-200/70 bg-white shadow-sm">
                <div className="border-b border-gray-100 bg-gray-50/70 px-4 py-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <h4 className="text-lg font-semibold text-gray-900">
                      Metric Configuration
                    </h4>
                    <p className="text-sm text-gray-500">
                      Click a category to expand. Toggle metrics on/off and
                      adjust weights within each category.
                    </p>
                  </div>
                </div>

                <div className="divide-y divide-gray-100/80">
                  {orderedCategories.map(categoryId => {
                    const categoryName =
                      getCategoryLabel(categoryId) ||
                      toReadableLabel(categoryId);
                    const summary = getCategorySummary(categoryId);
                    const isExpanded = expandedCategories.has(categoryId);
                    const categoryMetrics = metricsByCategory[categoryId] || [];
                    const categoryEnabled =
                      draftConfiguration.includedCategories.includes(
                        categoryId
                      );
                    return (
                      <div key={categoryId} className="bg-white px-4">
                        <button
                          type="button"
                          className="flex w-full items-center justify-between gap-3 py-3 text-left transition-colors hover:bg-gray-50/40"
                          onClick={() => toggleExpandedCategory(categoryId)}
                        >
                          <div className="flex items-center gap-2 text-[17px] font-semibold text-gray-800">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-gray-500" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-gray-500" />
                            )}
                            <span>{categoryName}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            {summary.summaryText ? (
                              <span className="hidden text-xs text-gray-500 sm:inline">
                                {summary.summaryText}
                              </span>
                            ) : null}
                            <div className="flex h-2 w-24 overflow-hidden rounded-full bg-gray-200 sm:w-28">
                              <div
                                className="bg-emerald-500"
                                style={{ width: `${summary.activePct}%` }}
                              />
                              <div
                                className="bg-amber-400"
                                style={{ width: `${summary.partialPct}%` }}
                              />
                              <div
                                className="bg-gray-400"
                                style={{ width: `${summary.noDataPct}%` }}
                              />
                            </div>
                          </div>
                        </button>

                        {isExpanded && (
                          <div className="mb-3 overflow-hidden rounded-lg border border-gray-100 bg-white">
                            <div className="grid grid-cols-12 bg-gray-50/70 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                              <div className="col-span-5">Metric</div>
                              <div className="col-span-2">Status</div>
                              <div className="col-span-1">Cadence</div>
                              <div className="col-span-2">Coverage</div>
                              <div className="col-span-1 text-center">
                                Include
                              </div>
                              <div className="col-span-1 text-center">
                                Weight
                              </div>
                            </div>
                            {categoryMetrics.map(metric => {
                              const statusDisplay =
                                getMetricCoverageDisplay(metric);
                              const metricIncluded = (
                                draftConfiguration.includedMetrics[
                                  categoryId
                                ] || []
                              ).includes(metric.metricId);
                              const metricWeight =
                                draftConfiguration.metricWeights[categoryId]?.[
                                  metric.metricId
                                ] ?? 0;
                              return (
                                <div
                                  key={metric.metricId}
                                  className="grid grid-cols-12 items-center border-t border-gray-100/80 px-4 py-2.5 text-sm"
                                >
                                  <div className="col-span-5 min-w-0">
                                    <div className="flex min-w-0 items-center gap-2">
                                      <p className="truncate font-medium text-gray-800">
                                        {metric.metricName}
                                      </p>
                                      {metric.isCustom && (
                                        <span className="shrink-0 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700">
                                          Custom
                                        </span>
                                      )}
                                    </div>
                                    {metric.description && (
                                      <p className="truncate pt-0.5 text-xs text-gray-500">
                                        {metric.description}
                                      </p>
                                    )}
                                  </div>
                                  <div className="col-span-2">
                                    <span
                                      className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${statusDisplay.pill}`}
                                    >
                                      {statusDisplay.label}
                                    </span>
                                  </div>
                                  <div className="col-span-1">
                                    <span className="inline-flex rounded-full bg-cyan-100 px-2 py-0.5 text-[11px] font-medium text-cyan-700">
                                      {metric.cadence}
                                    </span>
                                  </div>
                                  <div className="col-span-2 text-xs font-medium text-gray-600">
                                    {metric.coverageLabel}
                                  </div>
                                  <div className="col-span-1 flex justify-center">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleToggleMetric(
                                          categoryId,
                                          metric.metricId
                                        )
                                      }
                                      disabled={!categoryEnabled || isSaving}
                                      className={`relative h-6 w-11 rounded-full transition duration-200 ${
                                        metricIncluded
                                          ? "bg-violet-500"
                                          : "bg-gray-300"
                                      } ${
                                        !categoryEnabled || isSaving
                                          ? "cursor-not-allowed opacity-60"
                                          : ""
                                      }`}
                                      aria-label={`Toggle ${metric.metricName}`}
                                    >
                                      <span
                                        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${
                                          metricIncluded
                                            ? "left-[22px]"
                                            : "left-0.5"
                                        }`}
                                      />
                                    </button>
                                  </div>
                                  <div className="col-span-1 flex justify-center">
                                    <input
                                      type="number"
                                      min={0}
                                      max={100}
                                      step={1}
                                      value={
                                        metricIncluded && categoryEnabled
                                          ? metricWeight
                                          : 0
                                      }
                                      onChange={event =>
                                        handleMetricWeightChange(
                                          categoryId,
                                          metric.metricId,
                                          event.target.value
                                        )
                                      }
                                      onBlur={() =>
                                        handleMetricWeightBlur(categoryId)
                                      }
                                      className="w-16 rounded border border-gray-200 bg-white px-2 py-1 text-center text-sm text-gray-700 disabled:cursor-not-allowed disabled:bg-gray-100"
                                      disabled={
                                        !metricIncluded ||
                                        !categoryEnabled ||
                                        isSaving
                                      }
                                    />
                                  </div>
                                </div>
                              );
                            })}
                            <div className="border-t border-gray-100/80 bg-gray-50/50 px-4 py-2 text-xs">
                              <p className="mb-2 text-gray-500">
                                Enabled metric weights must total 100% within
                                this category. Disabled metrics count as 0.
                              </p>
                              <div className="flex flex-wrap items-center justify-between gap-3">
                                <span className="font-medium text-gray-700">
                                  Total:{" "}
                                  {metricWeightTotalByCategory[categoryId] ?? 0}
                                  %
                                  {(metricWeightTotalByCategory[categoryId] ??
                                    0) === 100
                                    ? " ✓"
                                    : ""}
                                </span>
                                {categoryEnabled &&
                                  metricConfigChangesByCategory[categoryId] && (
                                    <WeightRedistributionActions
                                      showRedistribute={
                                        (draftConfiguration.includedMetrics[
                                          categoryId
                                        ]?.length ?? 0) > 0
                                      }
                                      disabled={isSaving}
                                      onProRata={() =>
                                        redistributeIncludedMetricWeights(
                                          categoryId,
                                          "proRata"
                                        )
                                      }
                                      onEqual={() =>
                                        redistributeIncludedMetricWeights(
                                          categoryId,
                                          "equal"
                                        )
                                      }
                                      onDiscard={() =>
                                        handleDiscardMetricConfiguration(
                                          categoryId
                                        )
                                      }
                                    />
                                  )}
                              </div>
                              {metricWeightsTouchedByCategory[categoryId] &&
                                categoryEnabled &&
                                (draftConfiguration.includedMetrics[categoryId]
                                  ?.length ?? 0) > 0 &&
                                (metricWeightTotalByCategory[categoryId] ??
                                  0) !== 100 && (
                                  <p className="mt-2 text-red-700">
                                    Metric weights in this category must total
                                    exactly 100% (currently{" "}
                                    {metricWeightTotalByCategory[categoryId]}%).
                                  </p>
                                )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {activeSubTab === "ingestionCadence" && (
            <IngestionCadenceSection
              orgConfigurationData={orgConfigurationData}
              cadenceByMetricKey={cadenceByMetricKey}
              onCadenceChange={handleCadenceChange}
              isSaving={isSaving}
            />
          )}

          {activeSubTab !== "dataAudit" && (
            <div className="flex items-center justify-between">
              <Button
                variant="outline-primary"
                onClick={handleDiscardChanges}
                disabled={!hasAnyChanges || isSaving}
              >
                Discard Changes
              </Button>
              <Button
                variant="primary"
                onClick={() => void handleSaveChanges()}
                loading={isSaving}
                disabled={!hasAnyChanges || isSaving || !weightsValid}
                title={
                  !weightsValid
                    ? "Category and metric weights must each total 100% before saving"
                    : undefined
                }
              >
                Save Changes
              </Button>
            </div>
          )}
        </>
      )}

      {!isConfigLoading &&
        activeSubTab !== "dataAudit" &&
        orderedCategories.length === 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
            No category weights found in the current configuration.
          </div>
        )}
    </div>
  );
};

export default MetricsWeightsSection;
